// Session PJ1F (PJ1 Stage F): the supplier half of a purchase order. The office looks at the order as the supplier will receive it
// (a PDF), types or confirms the supplier's address, checks what the draft would hold, and a branded two-step confirm that names the
// recipient makes a GMAIL DRAFT. It is never sent from here: a person checks it in Gmail and sends it. The order carries nothing of
// the customer, and the platform refuses the draft while the items or notes name the customer (the reply is shown as it comes).
import { useState } from "react";
import { api, type PurchaseOrderListItem, type PurchaseOrderSupplierDraftPreview } from "../../../lib/api";
import { friendlyApiError } from "../../../lib/friendlyApiError";
import { ConfirmButton } from "../../../components/ConfirmButton";
import { Button, buttonClasses } from "../../../components/Button";
import { formatDate } from "../../../lib/format";

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green disabled:opacity-50 max-sm:min-h-11";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const confirmBtnCls = buttonClasses("primary", "sm", "max-sm:w-full");

function size(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export function SupplierOrderDraftPanel({ order, onDrafted }: { order: PurchaseOrderListItem; onDrafted?: (supplierDraftedAt: string) => void }) {
  const closed = order.status === "received" || order.status === "cancelled";
  const [to, setTo] = useState(order.supplierEmail ?? "");
  const [preview, setPreview] = useState<PurchaseOrderSupplierDraftPreview | null>(null);
  const [draftedAt, setDraftedAt] = useState<string | null>(order.supplierDraftedAt ?? null);
  const [justMade, setJustMade] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const addressOk = /^\S+@\S+\.\S+$/.test(to.trim());
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  // Session PJ2F3: the order is fetched first, so a refusal (the customer's name or contact details in what was typed) is said here,
  // never shown as a JSON reply in a new tab. Shown above the address, so it is seen on a received or cancelled order too.
  async function lookAtOrder() {
    setPdfBusy(true);
    setPdfError(null);
    try {
      await api.purchaseOrders.downloadPdf(order.id, order.reference);
    } catch (e) {
      setPdfError(friendlyApiError(e, "Could not get the order. Please try again."));
    } finally {
      setPdfBusy(false);
    }
  }

  async function check() {
    setBusy(true);
    setError(null);
    setJustMade(false);
    try {
      const r = await api.purchaseOrders.supplierDraft(order.id, { to: to.trim(), preview: true });
      if (r.preview) setPreview(r);
    } catch (e) {
      setError(friendlyApiError(e, "Could not check the draft."));
    } finally {
      setBusy(false);
    }
  }

  async function make() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.purchaseOrders.supplierDraft(order.id, { to: to.trim(), preview: false });
      if (!r.preview) {
        setDraftedAt(r.supplierDraftedAt);
        setJustMade(true);
        setPreview(null);
        onDrafted?.(r.supplierDraftedAt);
      }
    } catch (e) {
      setError(friendlyApiError(e, "Could not make the draft."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mb-4 rounded-ele border border-ele-hairline p-3 text-sm" aria-label="Order for the supplier" data-supplier-order-panel>
      <p className="mb-2 font-menu text-xs uppercase tracking-wide text-ele-blue">Order for the supplier</p>
      <p className="text-ele-body">
        A PDF of this order, as the supplier receives it, made into a Gmail draft. Nothing is sent: you check the draft in Gmail and send it yourself. It never names the customer.
      </p>
      <p className="mt-1 text-sm">
        <button type="button" className="text-ele-green underline-offset-2 hover:underline disabled:opacity-50" onClick={() => void lookAtOrder()} disabled={pdfBusy}>
          {pdfBusy ? "Getting the order..." : "Download the order to check it first (PDF)"}
        </button>
      </p>
      {pdfError ? <p role="alert" className="mt-2 rounded-ele border border-ele-danger px-3 py-2 text-ele-strong">{pdfError}</p> : null}

      {justMade ? (
        <p role="status" className="mt-3 rounded-ele border border-ele-green px-3 py-2 text-ele-strong">A Gmail draft is ready in info@ for you to check and send.</p>
      ) : draftedAt ? (
        <p className="mt-2 text-xs text-ele-muted">A draft of this order was made on {formatDate(draftedAt)}.</p>
      ) : null}

      {closed ? (
        <p className="mt-2 text-xs text-ele-muted">A received or cancelled order is not sent to the supplier again.</p>
      ) : (
        <>
          <div className="mt-3">
            <label className={labelCls} htmlFor={`po-supplier-to-${order.id}`}>Supplier's email address</label>
            <input
              id={`po-supplier-to-${order.id}`}
              type="email"
              className={inputCls}
              value={to}
              onChange={(e) => {
                setPreview(null);
                setTo(e.target.value);
              }}
            />
          </div>

          {preview ? (
            <div className="mt-3 rounded-ele border border-ele-card-border p-3 text-ele-body" role="status" data-draft-check>
              <p className="text-ele-strong">A draft to {preview.to}</p>
              <p className="text-xs text-ele-muted">Subject: {preview.subject}</p>
              <ul className="mt-2 list-disc space-y-0.5 pl-5">
                {preview.attachments.map((a) => (
                  <li key={a.name}>
                    {a.name} <span className="text-xs text-ele-muted">({size(a.bytes)})</span>
                  </li>
                ))}
              </ul>
              {preview.alreadyDraftedAt ? <p className="mt-1 text-[var(--ele-amber-text)]">A draft of this order was already made on {formatDate(preview.alreadyDraftedAt)}. This would be a second one.</p> : null}
            </div>
          ) : null}

          {!addressOk && !preview ? <p className="mt-2 text-xs text-ele-muted">Type the supplier's email address.</p> : null}
          {error ? <p role="alert" className="mt-3 rounded-ele border border-ele-danger px-3 py-2 text-ele-strong">{error}</p> : null}

          <div className="mt-3 flex flex-wrap items-center gap-3">
            {preview ? (
              <ConfirmButton
                onConfirm={() => void make()}
                label="Make the Gmail draft"
                confirmLabel={`Confirm: draft to ${preview.to}?`}
                busy={busy}
                busyLabel="Making the draft..."
                className={confirmBtnCls}
                armedClassName="!bg-ele-danger-fill !text-white"
                armedNote={
                  <p className="rounded-ele border border-ele-purple-accent bg-ele-surface p-2 text-xs text-ele-body">
                    Makes a Gmail draft to {preview.to} with the order attached. It is not sent.
                  </p>
                }
              />
            ) : (
              <Button size="sm" variant="secondary" className="max-sm:w-full" onClick={() => void check()} disabled={busy || !addressOk}>
                {busy ? "Checking..." : "Check the draft"}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
